import React from "react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";

import { titleize } from "utils/inflection";
import { useRoutes } from "utils/routes";

import LogLineContent from "./LogLineContent";
import { useLogLineDetailV2SuspenseQuery } from "./gql/LogLineDetail.v2.generated";

// An expanded log line. Fetches the full line via the detail query.

const DETAILS_BOX =
  "whitespace-normal break-words bg-white px-[10px] pt-[6px] pb-[5px] rounded-[3px] m-[5px_5px_5px_10px] text-black font-['Helvetica_Neue',Helvetica,Arial,sans-serif] leading-5 [&_span]:mr-[10px] [&_strong]:font-semibold";

function LogLineDetails({
  serverId,
  logLineId,
  databaseId: propDatabaseId,
}: {
  serverId: string;
  logLineId: string;
  databaseId?: string;
}) {
  const { serverLogs, serverRole, databaseLogs, databaseQuery } = useRoutes();
  const { data } = useLogLineDetailV2SuspenseQuery({
    variables: { serverId, id: logLineId },
  });

  const line = data.server.logLine;
  if (!line) {
    return <div className={DETAILS_BOX}>Log line no longer available.</div>;
  }

  const {
    logClassification,
    backendPid,
    postgresRoleId,
    postgresRoleName,
    databaseId,
    queryId,
    details,
    childText,
  } = line;
  // Focus the second after the line (focus has whole seconds) so it's on the first page of lines,
  // and expand it via the hash.
  const focusTs = moment(line.occurredAt).unix() + 1;
  const permalinkPath =
    (propDatabaseId
      ? databaseLogs(propDatabaseId, logClassification, focusTs)
      : serverLogs(serverId, logClassification, focusTs)) +
    "#" +
    line.id;

  return (
    <>
      <LogLineContent
        logLine={line}
        serverId={serverId}
        databaseId={databaseId ?? undefined}
      />
      {childText?.split("\n")?.map((text, key) => (
        <div className="ml-[10px]" key={key}>
          {text}
        </div>
      ))}
      <div className={DETAILS_BOX} key="details">
        <span>
          <strong>Permalink:</strong>{" "}
          <Link to={permalinkPath}>
            {window.location.origin + permalinkPath}
          </Link>
        </span>
        <br />
        {logClassification && (
          <LogLineDetailItem
            name="Classification"
            value={titleize(logClassification.toLowerCase().replace(/_/g, " "))}
          />
        )}
        <LogLineDetailItem name="Backend PID" value={backendPid} />
        {postgresRoleId && (
          <LogLineDetailItem
            name="Role"
            value={
              <Link to={serverRole(serverId, postgresRoleId)}>
                {postgresRoleName}
              </Link>
            }
          />
        )}
        {databaseId && queryId && (
          <LogLineDetailItem
            name="Query"
            value={
              <Link to={databaseQuery(databaseId, queryId)}>
                {"#" + queryId}
              </Link>
            }
          />
        )}
        <br />
        {Object.entries(details ?? {}).map(([k, v]) => (
          <LogLineDetailItem
            key={k}
            name={titleize(k.replace(/_/g, " "))}
            value={
              typeof v === "object" ? JSON.stringify(v) : (v as React.ReactNode)
            }
          />
        ))}
      </div>
    </>
  );
}

function LogLineDetailItem({
  name,
  value,
}: {
  name: string;
  value: React.ReactNode;
}) {
  let display = value;
  if (value === true) {
    display = "Yes";
  }
  if (value === false) {
    display = "No";
  }
  return (
    <span>
      <strong>{name}:</strong>
      &nbsp;
      {display}
    </span>
  );
}

export default LogLineDetails;
